import { Header } from 'components/Header'
import { PaymentCCContainer } from './styled'
import { useEffect, useState } from 'react'
import { RenderIf } from 'components/RenderIf'
import { Overwiew } from './Overview'
import { LoginSuggestion } from './LoginSuggestion'
import { PaymentSteps, ResultParams, ResultType } from './types'
import { Credentials } from './Credentials'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { Result } from './Result'
import { EmptyBlock } from '../CheckoutQRM/styled'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getNavLink } from 'featureSliced/shared/utils/getNavLink'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import { findCatalogProduct } from 'featureSliced/shared/utils/findCatalogProduct'
import {
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'
import { isPickupTimeAllowed } from 'featureSliced/shared/utils/getEarliestPickup'
import moment from 'moment'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'

export const PaymentCC = () => {
  const navigate = useNavigate()
  const dispatch = useMyDispatch()
  const t = useTranslations()
  const [searchParams] = useSearchParams()
  const isUserAuth = useMySelector((state) => state.auth.isLoggedIn)
  const bucketProducts = useMySelector((state) => state.bucket.products)
  const productList = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )
  const isProductsLoaded = useMySelector((state) => state.app.isProductsLoaded)
  const clickCollectConfig = useMySelector(
    (state) => state.app.clickCollectConfig
  )

  const [step, setStep] = useState<PaymentSteps>(PaymentSteps.OVEWVIEW)
  const [requestResult, setRequestResult] = useState<ResultType>('unknown')
  const [orderNr, setOrderNr] = useState<number>()

  useEffect(() => {
    if (!bucketProducts?.length) {
      navigate(getNavLink(searchParams, 'menu'))
    }
  }, [bucketProducts])

  // Backstop re-check on the way into checkout: the menu-load cleanup (Menu/index.tsx)
  // covers the common path, but a customer can also reach PaymentCC via the "Continue to
  // checkout" button from an already-loaded menu whose pickup time/catalogue changed
  // underneath the persisted cart. Remove what no longer qualifies and send the customer
  // back to the menu rather than let them pay for it (QRM-639).
  useEffect(() => {
    if (!isProductsLoaded || !bucketProducts?.length) return
    const { pickUpTime } = handleLocalStorage(['pickUpTime'])
    // Company-level rule first: if the stored pickup time no longer satisfies the
    // shop's lead-days/cutoff at all (config raised, or the date slipped into the
    // past while the tab sat open), no line in the cart can be ordered — clear it
    // and send the customer back to the menu, whose own guard reopens the time
    // wizard for a fresh pick.
    if (
      !isPickupTimeAllowed({
        now: moment(),
        pickUpTime,
        leadTimeDays: clickCollectConfig?.leadTimeDays,
        cutoff: clickCollectConfig?.leadTimeHours,
      })
    ) {
      dispatch(QRMBucketSlice.actions.clearBucket())
      dispatch(
        ToastsSlice.actions.openToast({
          message: t('clickCollect.leadDaysItemsRemoved'),
          type: 'error',
        })
      )
      navigate(getNavLink(searchParams, 'menu'))
      return
    }
    const exceededLines = bucketProducts.filter((line) => {
      const catalogProduct = findCatalogProduct(productList, line.productId)
      if (!catalogProduct) return false
      const leadDays = getItemLeadDays(catalogProduct.ClickCollectLeadDaysCount)
      return isItemLeadDaysExceeded({ leadDays, pickUpTime })
    })
    if (exceededLines.length) {
      exceededLines.forEach((line) =>
        dispatch(QRMBucketSlice.actions.removeProductFromBucket(line.uniqueId))
      )
      dispatch(
        ToastsSlice.actions.openToast({
          message: t('clickCollect.leadDaysItemsRemoved'),
          type: 'error',
        })
      )
      navigate(getNavLink(searchParams, 'menu'))
    }
  }, [isProductsLoaded]) // eslint-disable-line

  const handleOverwiewNextClick = () => {
    if (isUserAuth) {
      setStep(PaymentSteps.CREDENTIALS)
    } else {
      setStep(PaymentSteps.SUGGESTION)
    }
  }

  const moveToResult = (result: ResultType, orderNumberToSet?: number) => {
    setOrderNr(orderNumberToSet)
    setRequestResult(result)
    setStep(PaymentSteps.RESULT)
  }

  return (
    <PaymentCCContainer>
      <Header />
      <EmptyBlock />

      <RenderIf condition={step === PaymentSteps.OVEWVIEW}>
        <Overwiew moveToSuggestions={handleOverwiewNextClick} />
      </RenderIf>

      <RenderIf condition={step === PaymentSteps.SUGGESTION}>
        <LoginSuggestion
          moveToCredentials={() => setStep(PaymentSteps.CREDENTIALS)}
        />
      </RenderIf>

      <RenderIf condition={step === PaymentSteps.CREDENTIALS}>
        <Credentials moveToResult={moveToResult} />
      </RenderIf>

      <RenderIf condition={step === PaymentSteps.RESULT}>
        <Result result={requestResult} orderNr={orderNr} />
      </RenderIf>
    </PaymentCCContainer>
  )
}
